import { useMemo, useState, type FormEvent, type JSX } from 'react';
import {
  useCampaignPoll,
  useCampaigns,
  useCreateCampaign,
  useStopCampaign,
} from '../hooks/campaigns';
import { useAccounts } from '../hooks/accounts';
import { useFolders } from '../hooks/folders';
import type { CampaignLive, CampaignStatus } from '../api/types';

const DEFAULT_TEMPLATE =
  'Hey @{{username}}! Love your content. We work with brands looking for creators like you — interested in paid partnerships?';

export function CampaignsPanel(): JSX.Element {
  const { data: campaigns, isLoading } = useCampaigns();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <>
      <div className="dx-section-toolbar">
        <div className="dx-pane-meta">
          {campaigns ? `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}` : '—'}
        </div>
        <button className="dx-btn is-primary" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Cancel' : '+ New campaign'}
        </button>
      </div>

      {showCreate && (
        <CreateCampaignForm
          onCreated={(id) => {
            setShowCreate(false);
            setExpandedId(id);
          }}
        />
      )}

      {isLoading && <div className="dx-loading">Loading campaigns…</div>}

      {!isLoading && campaigns && campaigns.length === 0 && !showCreate && (
        <div className="dx-empty">
          <div className="dx-empty-title">No campaigns yet</div>
          <div>Create one above to start sending DMs.</div>
        </div>
      )}

      {!isLoading && campaigns && campaigns.length > 0 && (
        <div className="dx-campaign-list">
          {[...campaigns]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              />
            ))}
        </div>
      )}
    </>
  );
}

function CampaignCard({
  campaign,
  expanded,
  onToggle,
}: {
  campaign: CampaignLive;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const isActive = campaign.status === 'running' || campaign.status === 'pending';
  const live = useCampaignPoll(isActive || expanded ? campaign.id : null, campaign, campaign.folderId);
  const stopMutation = useStopCampaign();

  const status = live.status === 'idle' ? campaign.status : live.status;

  return (
    <div className={`dx-campaign-card ${isActive ? 'is-active' : ''}`}>
      <button type="button" className="dx-campaign-summary" onClick={onToggle}>
        <div>
          <div className="dx-username">{campaign.folderName}</div>
          <div className="dx-username-secondary">via {campaign.accountLabel}</div>
        </div>
        <div className="dx-campaign-stats">
          <Stat label="Sent" value={live.sent || campaign.sent} accent />
          <Stat label="Failed" value={live.failed || campaign.failed} />
          <Stat label="Total" value={campaign.totalLeads} />
          <StatusBadge status={status} />
        </div>
      </button>

      {expanded && (
        <div className="dx-campaign-detail">
          <div className="dx-campaign-progressbar">
            <div
              className="dx-campaign-progressbar-fill"
              style={{
                width: `${Math.min(100, ((live.sent + live.failed) / Math.max(1, live.totalLeads)) * 100)}%`,
              }}
            />
          </div>

          <div className="dx-log-stream" style={{ maxHeight: 240 }}>
            {live.logs.length === 0 && <div className="dx-log-line">No logs yet.</div>}
            {live.logs.map((log, i) => (
              <div key={i} className={`dx-log-line is-${log.type}`}>
                <span className="dx-log-time">{new Date(log.ts).toLocaleTimeString()}</span>
                <span className="dx-log-msg">{log.msg}</span>
              </div>
            ))}
          </div>

          {isActive && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="dx-btn"
                onClick={() => stopMutation.mutate(campaign.id)}
                disabled={stopMutation.isPending}
              >
                {stopMutation.isPending ? 'Stopping…' : 'Stop campaign'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateCampaignForm({ onCreated }: { onCreated: (id: string) => void }): JSX.Element {
  const { data: folders } = useFolders();
  const { data: accounts } = useAccounts();
  const createMutation = useCreateCampaign();

  const [folderId, setFolderId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [dmFilter, setDmFilter] = useState<'all' | 'not-sent' | 'failed'>('not-sent');
  const [dailyLimit, setDailyLimit] = useState(50);
  const [minDelay, setMinDelay] = useState(30);
  const [maxDelay, setMaxDelay] = useState(90);

  const activeAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.status === 'active'),
    [accounts],
  );

  function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!folderId || !accountId) return;
    createMutation.mutate(
      { folderId, accountId, template, dmFilter, dailyLimit, minDelay, maxDelay },
      { onSuccess: ({ campaignId }) => onCreated(campaignId) },
    );
  }

  return (
    <form className="dx-search-panel" onSubmit={submit}>
      <div className="dx-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <label className="dx-form-field">
          <span>Folder</span>
          <select
            className="dx-input"
            value={folderId}
            onChange={(ev) => setFolderId(ev.target.value)}
            required
          >
            <option value="">— select —</option>
            {folders?.map((f) => (
              <option key={f.publicId} value={f.publicId}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="dx-form-field">
          <span>Account</span>
          <select
            className="dx-input"
            value={accountId}
            onChange={(ev) => setAccountId(ev.target.value)}
            required
          >
            <option value="">— select —</option>
            {activeAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="dx-form-field">
          <span>DM filter</span>
          <select
            className="dx-input"
            value={dmFilter}
            onChange={(ev) => setDmFilter(ev.target.value as typeof dmFilter)}
          >
            <option value="all">All leads</option>
            <option value="not-sent">Not yet DMed</option>
            <option value="failed">Previously failed</option>
          </select>
        </label>
        <label className="dx-form-field">
          <span>Daily limit</span>
          <input
            className="dx-input"
            type="number"
            min={1}
            max={500}
            value={dailyLimit}
            onChange={(ev) => setDailyLimit(Number(ev.target.value) || 50)}
          />
        </label>
        <label className="dx-form-field">
          <span>Min delay (s)</span>
          <input
            className="dx-input"
            type="number"
            min={5}
            value={minDelay}
            onChange={(ev) => setMinDelay(Number(ev.target.value) || 30)}
          />
        </label>
        <label className="dx-form-field">
          <span>Max delay (s)</span>
          <input
            className="dx-input"
            type="number"
            min={10}
            value={maxDelay}
            onChange={(ev) => setMaxDelay(Number(ev.target.value) || 90)}
          />
        </label>
      </div>

      <label className="dx-modal-field">
        <span>Message template</span>
        <textarea
          className="dx-input"
          rows={3}
          value={template}
          onChange={(ev) => setTemplate(ev.target.value)}
          style={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}
        />
        <small className="dx-hint">
          Variables: <code>{`{{username}}`}</code>, <code>{`{{nickname}}`}</code>,{' '}
          <code>{`{{followers}}`}</code>, <code>{`{{avgViews}}`}</code>
        </small>
      </label>

      {createMutation.error && <div className="dx-error">{(createMutation.error as Error).message}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="submit"
          className="dx-btn is-primary"
          disabled={createMutation.isPending || !folderId || !accountId}
        >
          {createMutation.isPending ? 'Launching…' : 'Launch campaign'}
        </button>
      </div>
    </form>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }): JSX.Element {
  return (
    <div className={`dx-campaign-stat ${accent ? 'is-accent' : ''}`}>
      <div className="dx-campaign-stat-value">{value}</div>
      <div className="dx-campaign-stat-label">{label}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: CampaignStatus | 'idle' }): JSX.Element {
  const klass =
    status === 'done' ? 'is-converted' :
    status === 'running' ? 'is-contacted' :
    status === 'error' ? 'is-dead' :
    status === 'stopped' ? 'is-replied' :
    'is-new';
  return <span className={`dx-pill ${klass}`}>{status}</span>;
}
